import store from '@/store';
import axios from 'axios';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Mutations, Actions, Endpoints } from '@/ts/store/Constants';
import ErrorResponse from '@/ts/ErrorResponse';
import { IntegrationDataResponse, IntegrationTypeResponse, QueueIntegrationParameters } from '@/ts/HococoInterfaces';
import Integration from '@/ts/Integration';
import IntegrationData from '@/ts/IntegrationData';
import { Connection } from '@/configuration/Connection';
import { NotifyHelpers } from '@/ts/helpers/NotifyHelpers';

@Module({name: 'Integrations', namespaced: true})
export default class Integrations extends VuexModule {
    public integrations: Integration[] = [];
    public integrationMap: { [index: number]: number } = {};
    public allIntegrations: any[] = [];
    public currentIntegrationId: number = null;
    public currentIntegrationSingleData: IntegrationData = null;
    public integrationSearchOption: any = '';
    public addIntegrationVisibility: boolean = false;
    public previewIntegrationVisibility: boolean = false;
    public currentIntegrationOutputOverlayVisibility: boolean = false;
    public integrationUploadingStatus: boolean = false;
    public integrationUploadingPercentage: number = 0;

    @Mutation
    public RESET_INTEGRATIONS_STORE_STATE() {
        this.integrations                              = [];
        this.integrationMap                            = {};
        this.allIntegrations                           = [];
        this.currentIntegrationId                      = null;
        this.currentIntegrationSingleData              = null;
        this.integrationSearchOption                   = '';
        this.addIntegrationVisibility                  = false;
        this.previewIntegrationVisibility              = false;
        this.currentIntegrationOutputOverlayVisibility = false;
        this.integrationUploadingStatus                = false;
        this.integrationUploadingPercentage            = 0;
    }

    get getIntegrations() {
        return this.integrations;
    }

    get currentIntegrationData() {
        if (this.currentIntegrationId === null) {
            return [];
        }

        return this.integrations[this.integrationMap[this.currentIntegrationId]].integrationData;
    }

    get getIntegrationSearchOption() {
        return this.integrationSearchOption;
    }

    get getCurrentIntegration(): Integration {
        return (this.currentIntegrationId !== null)
            ? this.integrations[this.integrationMap[this.currentIntegrationId]]
            : null;
    }

    get getAddIntegrationVisibility() {
        return this.addIntegrationVisibility;
    }

    get getPreviewIntegrationVisibility() {
        return this.previewIntegrationVisibility;
    }

    get getIntegrationUploadingStatus() {
        return this.integrationUploadingStatus;
    }

    get getIntegrationUploadingPercentage() {
        return this.integrationUploadingPercentage;
    }

    get getCurrentIntegrationSingleData(): IntegrationData {
        return this.currentIntegrationSingleData;
    }

    get getCurrentIntegrationOutputOverlayVisibility(): boolean {
        return this.currentIntegrationOutputOverlayVisibility;
    }

    @Action
    public INITIALIZE_INTEGRATIONS_SOCKET(parameters: QueueIntegrationParameters) {
        if (!Connection.socketEnabled) {
            return;
        }

        const integrationChannel = 'integration.' + parameters.guid;

        parameters.vue.$echo.private(integrationChannel)
            .listen(('.integration.status-change'), (payload: IntegrationDataResponse) => {
                store.commit(Mutations.UPDATE_INTEGRATION_DATA, payload);
            });
    }


    @Action
    public FETCH_INTEGRATION_TYPES() {
        axios.get(Endpoints.Admin.Integration.CORPORATION_INTEGRATIONS)
            .then((response: any) => {
                store.commit(Mutations.SET_INTEGRATION_TYPES, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public RESET_ACTIVE_INTEGRATION_SORTS(column: string) {
        const sorts = document.querySelectorAll('.hococo-admin-integration-header i');
        sorts.forEach( (sort) => {
            if (sort.getAttribute('data-sort-key') !== column) {
                if (!sort.classList.contains('fa-angle-down')) {
                    sort.classList.add('fa-angle-down');
                }
                if (sort.classList.contains('fa-angle-up')) {
                    sort.classList.remove('fa-angle-up');
                }
            }
        });
    }

    @Action
    public FETCH_CURRENT_INTEGRATION_DATA() {
        const integrationId = Integrations.state.currentIntegrationId;

        axios.get(Endpoints.Admin.Integration.INTEGRATION_DATA(integrationId))
            .then((response: any) => {
                store.commit(Mutations.SET_CURRENT_INTEGRATION_DATA, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public UPLOAD_INTEGRATION_DATA(data: any) {
        store.commit(Mutations.SET_INTEGRATION_UPLOAD_STATUS, true);

        const formData: FormData = new FormData();
        const integrationId = Integrations.state.currentIntegrationId;

        formData.append('file', data.file);

        axios.post(Endpoints.Admin.Integration.INTEGRATION_DATA(integrationId), formData, {
            onUploadProgress: (progressEvent: ProgressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                store.commit(Mutations.SET_INTEGRATION_UPLOAD_PERCENTAGE, percentCompleted);
            },
        })
            .then(() => {
                store.commit(Mutations.SET_INTEGRATION_UPLOAD_STATUS, false);
                store.commit(Mutations.SET_INTEGRATION_UPLOAD_PERCENTAGE, 0);
                store.commit(Mutations.SET_ADD_INTEGRATION_VISIBILITY, false);

                store.dispatch(Actions.FETCH_CURRENT_INTEGRATION_DATA);
                NotifyHelpers.notify('file_has_been_successfully_uploaded', 'positive');
            })
            .catch((error) => {
                store.commit(Mutations.SET_INTEGRATION_UPLOAD_PERCENTAGE, 0);
                store.commit(Mutations.SET_INTEGRATION_UPLOAD_STATUS, false);

                NotifyHelpers.notify('error_occurred_on_file_upload', 'negative');

                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    console.log(error.response.data);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public QUEUE_INTEGRATION_DATA_IMPORT(parameters: QueueIntegrationParameters) {
        const integrationId = Integrations.state.currentIntegrationId;

        store.dispatch(Actions.INITIALIZE_INTEGRATIONS_SOCKET, parameters);

        axios.post(Endpoints.Admin.Integration.QUEUE_INTEGRATION_DATA_IMPORT(integrationId, parameters.guid))
            .then((result) => {
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Mutation
    public SET_INTEGRATION_TYPES(integrations: IntegrationTypeResponse[]) {
        if (typeof integrations === 'string') {
            return;
        }

        for (const integration of integrations) {
            if (this.integrationMap.hasOwnProperty(integration.id)) {
                this.integrations[this.integrationMap[integration.id]].updateData(integration);
                continue;
            }

            this.integrationMap[integration.id] = this.integrations.push(Integration.FromResponse(integration)) - 1;
        }
    }

    @Mutation
    public SET_CURRENT_INTEGRATION_DATA(integrationData: IntegrationDataResponse[]) {
        const currentIntegration = this.integrations[this.integrationMap[this.currentIntegrationId]];
        currentIntegration.synchronizeData(integrationData);
    }

    @Mutation
    public UPDATE_INTEGRATION_DATA(data: IntegrationDataResponse) {
        this.integrations[this.integrationMap[data.typeId]].synchronizeData([data]);
    }

    @Mutation
    public SET_ALL_INTEGRATIONS(data: any) {
        this.allIntegrations = data;
    }

    @Mutation
    public SET_CURRENT_INTEGRATION(integrationId: number) {
        this.currentIntegrationId = integrationId;
    }

    @Mutation
    public SET_ADD_INTEGRATION_VISIBILITY(visibility: boolean) {
        this.addIntegrationVisibility = visibility;
    }

    @Mutation
    public SET_INTEGRATION_SEARCH_OPTION(search: any) {
        this.integrationSearchOption = search;
    }

    @Mutation
    public SET_PREVIEW_INTEGRATION_VISIBILITY(visibility: boolean) {
        this.previewIntegrationVisibility = visibility;
    }

    @Mutation
    public SET_INTEGRATION_UPLOAD_STATUS(status: boolean) {
        this.integrationUploadingStatus = status;
    }

    @Mutation
    public SET_INTEGRATION_UPLOAD_PERCENTAGE(percent: number) {
        this.integrationUploadingPercentage = percent;
    }

    @Mutation
    public SET_CURRENT_INTEGRATION_SINGLE_DATA_OUTPUT(integrationData: IntegrationData) {
        this.currentIntegrationSingleData = integrationData;
    }

    @Mutation
    public SET_CURRENT_INTEGRATION_OUTPUT_OVERLAY_VISIBILITY(visibility: boolean) {
        this.currentIntegrationOutputOverlayVisibility = visibility;
    }

    @Mutation
    public RESET_INTEGRATIONS() {
        this.integrations = [];
        this.integrationMap = {};
    }
}
